/**
 * AI Status Indicator Component
 * ==============================
 * Shows AI status, confidence, and escalation state for conversations
 */

import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  IconButton,
  Stack
} from '@mui/material';
import {
  SmartToy,
  Person,
  Warning,
  TrendingUp,
  TrendingDown,
  Circle
} from '@mui/icons-material';

interface AIStatusIndicatorProps {
  aiEnabled: boolean;
  confidence?: number;
  escalated?: boolean;
  escalationReason?: string;
  isProcessing?: boolean;
  onToggle?: () => void;
  showDetails?: boolean;
}

export const AIStatusIndicator: React.FC<AIStatusIndicatorProps> = ({
  aiEnabled,
  confidence,
  escalated,
  escalationReason,
  isProcessing = false,
  onToggle,
  showDetails = true
}) => {
  // Determine confidence color
  const getConfidenceColor = (conf?: number): 'success' | 'warning' | 'error' | 'default' => {
    if (!conf) return 'default';
    if (conf >= 0.8) return 'success';
    if (conf >= 0.6) return 'warning';
    return 'error';
  };

  // Determine confidence icon
  const getConfidenceIcon = (conf?: number): React.ReactElement | undefined => {
    if (!conf) return undefined;
    if (conf >= 0.7) return <TrendingUp fontSize="small" />;
    return <TrendingDown fontSize="small" />;
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Main AI Status Chip */}
      <Tooltip
        title={
          escalated
            ? `Escalated: ${escalationReason || 'Unknown reason'}`
            : aiEnabled
            ? 'AI is handling this conversation'
            : 'Human agent is handling this conversation'
        }
      >
        <Chip
          icon={
            isProcessing ? (
              <CircularProgress size={16} color="inherit" />
            ) : aiEnabled ? (
              <SmartToy fontSize="small" />
            ) : (
              <Person fontSize="small" />
            )
          }
          label={
            isProcessing
              ? 'AI Processing...'
              : escalated
              ? 'Escalated'
              : aiEnabled
              ? 'AI'
              : 'Human'
          }
          color={
            escalated ? 'error' : aiEnabled ? 'primary' : 'default'
          }
          size="small"
          onClick={onToggle}
          sx={{
            cursor: onToggle ? 'pointer' : 'default',
            fontWeight: 600
          }}
        />
      </Tooltip>

      {/* Confidence Indicator */}
      {showDetails && confidence !== undefined && aiEnabled && (
        <Tooltip title={`AI Confidence: ${(confidence * 100).toFixed(0)}%`}>
          <Chip
            icon={getConfidenceIcon(confidence)}
            label={`${(confidence * 100).toFixed(0)}%`}
            color={getConfidenceColor(confidence)}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}

      {/* Escalation Warning */}
      {escalated && (
        <Tooltip title={escalationReason || 'Escalated to human'}>
          <Warning color="error" fontSize="small" />
        </Tooltip>
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Circle sx={{ fontSize: 8, color: 'primary.main', animation: 'pulse 1.5s infinite' }} />
          <Circle sx={{ fontSize: 8, color: 'primary.main', animation: 'pulse 1.5s infinite 0.3s' }} />
          <Circle sx={{ fontSize: 8, color: 'primary.main', animation: 'pulse 1.5s infinite 0.6s' }} />
        </Box>
      )}
    </Stack>
  );
};

export default AIStatusIndicator;
